import React from 'react';
import { ActivityIndicator, Pressable } from 'react-native';

import { Box, useTheme } from 'app/shopify/theme';

import { ArrowRightIcon } from './ArrowRightIcon';

type IconButtonVariant = 'primary' | 'secondary' | 'secondaryNeutral' | 'tertiary' | 'ghost' | 'ghostNeutral';
type IconButtonSize = 'small' | 'medium' | 'large';
type IconButtonState = 'enabled' | 'hover' | 'pressed' | 'focus' | 'disabled' | 'loading';

export interface IconButtonProps {
  onPress?: () => void;
  variant?: IconButtonVariant;
  size?: IconButtonSize;
  state?: IconButtonState;
  disabled?: boolean;
  inverted?: boolean;
  icon?: React.ReactNode;
}

const BUTTON_SIZE_FALLBACK: Record<IconButtonSize, number> = {
  small: 32,
  medium: 40,
  large: 48,
};

const SIZING_TOKEN_BY_SIZE = {
  small: 'sizing.component.button.height.small',
  medium: 'sizing.component.button.height.medium',
  large: 'sizing.component.button.height.large-icn-button',
} as const satisfies Record<IconButtonSize, string>;

const ICON_SIZE_BY_SIZE: Record<IconButtonSize, number> = {
  small: 20,
  medium: 24,
  large: 24,
};

const SPINNER_SIZE_BY_SIZE: Record<IconButtonSize, 'small' | 'large'> = {
  small: 'small',
  medium: 'large',
  large: 'large',
};

interface PressableInteractionState {
  pressed: boolean;
  hovered?: boolean;
}

type IconButtonTheme = ReturnType<typeof useTheme>;

function getIconButtonOverlayColor({
  inverted,
  showHover,
  showPressed,
  theme,
  variant,
}: {
  inverted: boolean;
  showHover: boolean;
  showPressed: boolean;
  theme: IconButtonTheme;
  variant: IconButtonVariant;
}) {
  if (!showPressed && !showHover) return undefined;
  if (showPressed) {
    if (variant === 'secondary' || variant === 'secondaryNeutral') return theme.colors['state-layer.press-alt'];
    if (variant === 'ghostNeutral') return theme.colors['state-layer.press'];
    return theme.colors['state-layer.press-brand'];
  }
  if (variant === 'secondary' || variant === 'secondaryNeutral') return theme.colors['state-layer.hover-alt'];
  if (variant === 'ghostNeutral') return theme.colors['state-layer.hover'];
  if (inverted && (variant === 'tertiary' || variant === 'ghost')) return theme.colors['state-layer.inverse.hover'];
  return theme.colors['state-layer.hover-brand'];
}

function getIconButtonPresentation({
  disabled,
  inverted,
  size,
  state,
  theme,
  variant,
}: Required<Pick<IconButtonProps, 'disabled' | 'inverted' | 'size' | 'variant'>> &
  Pick<IconButtonProps, 'state'> & { theme: IconButtonTheme }) {
  const isInteractive = state === undefined || state === 'enabled';
  const tokenValue = theme.tokens.sizing[SIZING_TOKEN_BY_SIZE[size]];
  const parsedSize = tokenValue ? Number(tokenValue) : NaN;
  return {
    backgroundColor:
      variant === 'primary'
        ? theme.colors[inverted ? 'component.button.bg.inverted' : 'component.button.bg.primary']
        : variant === 'secondary'
          ? theme.colors['component.button.bg.secondary']
          : variant === 'secondaryNeutral'
            ? theme.colors['component.button.bg.tertiary']
            : 'transparent',
    borderColor:
      variant === 'tertiary' ? theme.colors[inverted ? 'border.inverse.primary' : 'border.primary'] : 'transparent',
    borderWidth: variant === 'tertiary' ? 1 : 0,
    buttonSize: Number.isFinite(parsedSize) ? parsedSize : BUTTON_SIZE_FALLBACK[size],
    iconColor:
      variant === 'primary'
        ? theme.colors[inverted ? 'text.brand' : 'text.inverse.primary']
        : variant === 'secondary' || variant === 'secondaryNeutral' || variant === 'ghostNeutral'
          ? theme.colors['text.primary']
          : inverted
            ? theme.colors['text.inverse.primary']
            : theme.colors['text.brand'],
    isDisabled: isInteractive ? disabled : state === 'disabled',
    isFocus: state === 'focus',
    isInteractive,
    isLoading: state === 'loading',
  };
}

function IconButtonInner({
  icon,
  interaction,
  inverted,
  presentation,
  size,
  state,
  theme,
  variant,
}: {
  icon?: React.ReactNode;
  interaction?: PressableInteractionState;
  inverted: boolean;
  presentation: ReturnType<typeof getIconButtonPresentation>;
  size: IconButtonSize;
  state?: IconButtonState;
  theme: IconButtonTheme;
  variant: IconButtonVariant;
}) {
  const pressed = interaction?.pressed ?? false;
  const hovered = interaction?.hovered ?? false;
  const showPressed = presentation.isInteractive ? pressed : state === 'pressed';
  const showHover = presentation.isInteractive ? hovered && !pressed : state === 'hover';
  const overlayColor = getIconButtonOverlayColor({ inverted, showHover, showPressed, theme, variant });

  return (
    <Box
      alignItems="center"
      borderRadius="radius.full"
      justifyContent="center"
      style={{
        width: presentation.buttonSize,
        height: presentation.buttonSize,
        backgroundColor: presentation.backgroundColor,
        borderColor: presentation.borderColor,
        borderWidth: presentation.borderWidth,
        opacity: presentation.isDisabled ? 0.4 : 1,
      }}
    >
      {overlayColor ? (
        <Box
          borderRadius="radius.full"
          bottom={0}
          left={0}
          position="absolute"
          right={0}
          style={{ backgroundColor: overlayColor, zIndex: 0 }}
          top={0}
        />
      ) : null}
      <Box alignItems="center" justifyContent="center" style={{ zIndex: 1 }}>
        {presentation.isLoading ? (
          <ActivityIndicator color={presentation.iconColor} size={SPINNER_SIZE_BY_SIZE[size]} />
        ) : (
          (icon ?? <ArrowRightIcon color={presentation.iconColor} size={ICON_SIZE_BY_SIZE[size]} />)
        )}
      </Box>
      {presentation.isFocus ? (
        <Box
          borderRadius="radius.full"
          bottom={-4}
          left={-4}
          position="absolute"
          right={-4}
          style={{ borderWidth: 2, borderColor: theme.colors['border.focused'] }}
          top={-4}
        />
      ) : null}
    </Box>
  );
}

export function IconButton({
  onPress,
  variant = 'primary',
  size = 'large',
  state,
  disabled = false,
  inverted = false,
  icon,
}: IconButtonProps) {
  const theme = useTheme();
  const presentation = getIconButtonPresentation({ disabled, inverted, size, state, theme, variant });

  if (presentation.isInteractive) {
    return (
      <Pressable disabled={presentation.isDisabled || presentation.isLoading} onPress={onPress}>
        {(pressableState) => (
          <IconButtonInner
            icon={icon}
            interaction={{
              pressed: pressableState.pressed,
              hovered: 'hovered' in pressableState ? (pressableState as PressableInteractionState).hovered : false,
            }}
            inverted={inverted}
            presentation={presentation}
            size={size}
            state={state}
            theme={theme}
            variant={variant}
          />
        )}
      </Pressable>
    );
  }

  return (
    <Pressable disabled={presentation.isDisabled || presentation.isLoading} onPress={onPress}>
      <IconButtonInner
        icon={icon}
        inverted={inverted}
        presentation={presentation}
        size={size}
        state={state}
        theme={theme}
        variant={variant}
      />
    </Pressable>
  );
}
